import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";

export default function Teams() {
    const [teams, setTeams] = useState(null);

    useEffect(() => {
        api.get("/teams").then((r) => setTeams(r.data)).catch(() => setTeams([]));
    }, []);

    return (
        <div data-testid="teams-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#FF5500] mb-3">The Squads</p>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white">Teams</h1>
            <p className="mt-3 text-slate-400 text-sm max-w-lg">
                Every franchise in the league — records, rosters and colors.
            </p>

            {teams === null ? (
                <p className="mt-16 text-slate-500 font-stat text-sm">Loading teams…</p>
            ) : teams.length === 0 ? (
                <div data-testid="teams-empty" className="mt-16 card-arena rounded-2xl p-12 text-center">
                    <p className="font-display font-bold uppercase text-xl text-slate-300">No teams yet</p>
                    <p className="text-sm text-slate-500 mt-2">
                        The league office hasn't registered any franchises. Check back soon.
                    </p>
                </div>
            ) : (
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {teams.map((t, i) => (
                        <motion.div
                            key={t.id}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                        >
                            <Link
                                to={`/teams/${t.id}`}
                                data-testid={`team-card-${t.id}`}
                                className="card-arena rounded-2xl p-6 block group"
                            >
                                <div className="flex items-start justify-between">
                                    <TeamCrest team={t} size="xl" />
                                    <div className="text-right">
                                        {t.record?.rank && (
                                            <span className="font-stat text-xs text-[#FFB800]">#{t.record.rank}</span>
                                        )}
                                        <p className="font-stat font-bold text-2xl text-white mt-1">
                                            {t.record?.w ?? 0}–{t.record?.l ?? 0}
                                        </p>
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                                            W – L
                                        </p>
                                    </div>
                                </div>
                                <h3 className="font-display font-black uppercase text-2xl text-white mt-5 group-hover:text-[#FF5500] transition-colors duration-200">
                                    {t.name}
                                </h3>
                                <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">{t.city}</p>
                                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#262638]">
                                    <span className="flex items-center gap-1.5 text-xs text-slate-400">
                                        <Users className="w-3.5 h-3.5 text-[#FFB800]" /> {t.roster_count} players
                                    </span>
                                    <span className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-widest text-[#FF5500]">
                                        Roster <ArrowUpRight className="w-3.5 h-3.5" />
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}
